import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle, X, Hourglass, ForkKnife, SignIn, SignOut as SignOutIcon, ClockCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const TYPE_LABEL = {
  in: { label: "Entrada", Icon: SignIn, color: "#00873D" },
  lunch_out: { label: "Salida a comer", Icon: ForkKnife, color: "#FFC000" },
  lunch_in: { label: "Regreso de comida", Icon: ForkKnife, color: "#1E88E5" },
  out: { label: "Salida", Icon: SignOutIcon, color: "#E0000F" },
};

const STATUS_LABEL = {
  pending: { label: "Pendiente", color: "#FFC000", bg: "bg-amber-100 text-amber-800" },
  approved: { label: "Aprobada", color: "#00873D", bg: "bg-emerald-100 text-emerald-800" },
  rejected: { label: "Rechazada", color: "#E0000F", bg: "bg-red-100 text-red-800" },
};

export default function HrAuthorizations() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/hr/authorizations", { params: { status: statusFilter } });
      setItems(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error cargando solicitudes");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const decide = async (id, decision) => {
    let reason = "";
    if (decision === "rejected") {
      reason = window.prompt("Motivo del rechazo (opcional):") || "";
    }
    setBusy(id);
    try {
      await api.post(`/hr/authorizations/${id}/decide`, { decision, decision_reason: reason });
      toast.success(decision === "approved" ? "Autorización aprobada · checada registrada" : "Solicitud rechazada");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error procesando solicitud");
    } finally { setBusy(null); }
  };

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono-data text-xs tracking-widest text-zinc-500 mb-1">RH · BANDEJA</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight">Autorizaciones</h1>
          <p className="text-zinc-500 mt-1 text-sm">Empleados que escanearon fuera de horario y solicitan tu visto bueno</p>
        </div>
        <div className="inline-flex border border-zinc-200 rounded overflow-hidden text-xs" data-testid="auth-status-filter">
          {[["pending", "Pendientes"], ["approved", "Aprobadas"], ["rejected", "Rechazadas"], ["all", "Todas"]].map(([v, l]) => (
            <button key={v} onClick={() => setStatusFilter(v)} className={`px-3 py-2 ${statusFilter === v ? "bg-zinc-900 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`} data-testid={`auth-filter-${v}`}>{l}</button>
          ))}
        </div>
      </header>

      {loading ? (
        <div className="p-12 text-center text-zinc-400">Cargando…</div>
      ) : items.length === 0 ? (
        <div className="p-16 border border-dashed border-emerald-300 rounded bg-emerald-50/50 text-center" data-testid="auth-empty">
          <CheckCircle size={40} weight="duotone" className="text-emerald-500 mx-auto"/>
          <div className="mt-2 font-bold text-emerald-800">Sin solicitudes</div>
          <div className="text-sm text-zinc-500">Cuando algún empleado escanee fuera de horario, su solicitud aparecerá aquí.</div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="auth-list">
          {items.map((a) => {
            const meta = TYPE_LABEL[a.requested_type] || TYPE_LABEL.in;
            const status = STATUS_LABEL[a.status] || STATUS_LABEL.pending;
            const Icon = meta.Icon;
            const time = new Date(a.requested_at).toLocaleTimeString("es-MX", { timeZone: "America/Tijuana", hour: "2-digit", minute: "2-digit", hour12: false });
            const date = new Date(a.requested_at).toLocaleDateString("es-MX", { timeZone: "America/Tijuana", weekday: "short", day: "2-digit", month: "short" });
            return (
              <div key={a.id} className="p-4 border border-zinc-200 rounded bg-white flex flex-col md:flex-row gap-4" data-testid={`auth-row-${a.id}`}>
                {a.photo_url ? (
                  <img src={a.photo_url} alt={a.employee_name} className="w-24 h-24 object-cover rounded shrink-0"/>
                ) : (
                  <div className="w-24 h-24 bg-zinc-100 rounded flex items-center justify-center text-zinc-400 shrink-0">
                    <Hourglass size={28}/>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading text-lg font-bold">{a.employee_name}</span>
                    <span className="text-xs text-zinc-400">· {a.employee_area || "sin área"}</span>
                    <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold rounded ${status.bg}`}>{status.label}</span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-1.5 text-sm">
                    <Icon size={14} weight="duotone" style={{ color: meta.color }}/>
                    <span className="font-semibold" style={{ color: meta.color }}>{meta.label}</span>
                    <span className="text-zinc-400">·</span>
                    <span className="font-mono-data">{date} {time}</span>
                  </div>
                  <div className="mt-2 p-2 bg-amber-50 border-l-2 border-amber-400 text-sm text-zinc-700">
                    <span className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Motivo</span>
                    <p>{a.reason}</p>
                  </div>
                  {a.status !== "pending" && (
                    <div className="mt-2 text-[11px] text-zinc-500">
                      {status.label} por <b>{a.decided_by_name || a.decided_by_email}</b> el {new Date(a.decided_at).toLocaleString("es-MX", { timeZone: "America/Tijuana" })}
                      {a.decision_reason && <> · &ldquo;{a.decision_reason}&rdquo;</>}
                    </div>
                  )}
                </div>
                {a.status === "pending" && (
                  <div className="flex md:flex-col gap-2 shrink-0">
                    <Button onClick={() => decide(a.id, "approved")} disabled={busy === a.id} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1" data-testid={`auth-approve-${a.id}`}>
                      <CheckCircle size={14}/> Aprobar
                    </Button>
                    <Button onClick={() => decide(a.id, "rejected")} disabled={busy === a.id} variant="outline" className="border-red-300 text-red-700 hover:bg-red-50 gap-1" data-testid={`auth-reject-${a.id}`}>
                      <X size={14}/> Rechazar
                    </Button>
                  </div>
                )}
                {a.status === "approved" && (
                  <div className="shrink-0 text-emerald-700 inline-flex items-center gap-1 self-start">
                    <ClockCounterClockwise size={16}/> <span className="text-xs">Checada registrada</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
